(ns app.front.view.bond.subs
  (:require [app.domain.bond.model :as bond-model]
            [app.domain.util :as du]
            [app.front.dashlets :as dashlets]
            [app.front.entities :as entities]
            [app.front.task :as task]
            [app.front.validate :as validate]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.bond.new :as-alias bond-new]
            [app.front.view.bond.util :as bond-util]
            [app.front.view.bond.view :as-alias bond-view]
            [app.rich-text :as rich-text]
            [core.permission.resolver :as permission-resolver]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]))

(defn- compute-effective-error-messages
  "Computes error messages for a bond edit form, using relaxed (invite-flow)
   validation rules for pending-invite bonds."
  [required-fields bond]
  (let [pending-invite? (du/in-state? :bond.state/pending-invite bond)]
    (bond-util/effective-error-messages
      {:required-fields required-fields
       :owner-optional? pending-invite?
       :invite-flow?    pending-invite?})))

(rf/reg-sub ::can-edit-bond-attachments?
  (fn [[_ bond-id]]
    [(rf/subscribe [::entities/entity bond-id])
     (rf/subscribe [:session/user])])
  (fn [[bond user] _]
    (when bond
      (bond-model/can-edit-bond-attachments? bond user))))

(rf/reg-sub ::bond
  (fn [[_ bond-id]]
    (rf/subscribe [::entities/entity bond-id]))
  :-> identity)

;; Get current bond from route (via bond-view state)
(rf/reg-sub ::current-bond-id
  (fn [db _]
    (-> db ::bond-view/state :bond-id)))

(rf/reg-sub ::current-bond
  :<- [::current-bond-id]
  :<- [::bond-new/created-bond-id]
  :<- [::entities/context]
  (fn [[current-bond-id new-bond-id ctx] _]
    (cond
      new-bond-id     (entities/find-entity ctx {:db/id new-bond-id})
      current-bond-id (entities/find-entity ctx {:bond/id current-bond-id}))))

;; Bond group from current bond - follows property pattern
(rf/reg-sub ::current-bond-group
  :<- [::current-bond]
  :-> :bond/group)

;; Attachment types from bond's group
(rf/reg-sub ::current-attachment-types
  :<- [::current-bond-group]
  :-> :group/available-bond-attachments)

;; Attachment types with i18n - matches property pattern signature (no params)
;; Uses routing context to get current bond
(rf/reg-sub ::attachment-types-with-i18n
  :<- [::current-attachment-types]
  :<- [:language]
  (fn [[attachment-types language] _]
    (->> attachment-types
         (map (juxt #(attachment-common/attachment-type-label % language) :db/id))
         (sort-by first))))

;; Close bond modal state
(def ^:private close-state (interceptors/path ::close))

(rf/reg-sub ::close :-> ::close)
(rf/reg-sub ::close-reason :<- [::close] :-> :reason)
(rf/reg-sub ::close-saving? :<- [::close] :-> :saving?)

(rf/reg-sub ::close-open?
  :<- [::close]
  (fn [{:keys [open? bond-id]} [_ entity-id]]
    (and open? (= entity-id bond-id))))

(rf/reg-sub ::close-save-disabled?
  :<- [::close-saving?]
  :<- [::close-reason]
  (fn [[close-saving? close-reason]]
    (or close-saving?
        (rich-text/empty-html-rich-text? close-reason))))

(rf/reg-event-db ::open-close close-state
  (fn [state [_ bond-entity-id]]
    (assoc state :open? true :bond-id bond-entity-id :reason "" :saving? false)))

(rf/reg-event-db ::cancel-close close-state
  (fn [state _]
    (dissoc state :open? :reason :saving?)))

(rf/reg-event-db ::set-close-reason close-state
  (fn [state [_ v]]
    (assoc state :reason v)))

(rf/reg-event-fx ::submit-close close-state
  (fn [{state :db} [_ bond-entity-id reason]]
    {:comm/command {:command    :bond/close-bond
                    :method     :post
                    :data       {:bond-id bond-entity-id
                                 :reason  reason}
                    :on-success [::submit-close.success]
                    :on-error   [::submit-close.error]}
     :db           (assoc state :saving? true)}))

(rf/reg-event-fx ::submit-close.success close-state
  (fn [{state :db} [_ status entities]]
    {:fx [[:dispatch [::entities/apply-changes status entities]]
          [:dispatch [:notifications/add :toast :success :success :bond/closed-successfully]]]
     :db (dissoc state :open? :reason :saving?)}))

(rf/reg-event-db ::submit-close.error close-state
  (fn [state _]
    (assoc state :saving? false)))

;; Reopen bond
(rf/reg-event-fx ::reopen-bond
  (fn [_ [_ bond-entity-id]]
    {:comm/command {:command    :bond/reopen-bond
                    :method     :post
                    :data       {:bond-id bond-entity-id}
                    :on-success [::reopen-bond.success]}}))

(rf/reg-event-fx ::reopen-bond.success
  (fn [_ [_ status entities]]
    {:fx [[:dispatch [::entities/apply-changes status entities]]
          [:dispatch [:notifications/add :toast :success :success :bond/reopened-successfully]]]}))

(rf/reg-event-fx ::cancel-bond-invitation
  (fn [_ [_ bond-entity-id]]
    {:dispatch [:comm/command-monitored
                {:command    :bond/cancel-bond-invitation
                 :method     :post
                 :data       {:bond-id bond-entity-id}
                 :on-success [::cancel-bond-invitation.success]
                 :notify     {:success :bond/invite-cancelled-successfully}}]}))

(rf/reg-event-fx ::cancel-bond-invitation.success
  (fn [_ [_ status entities]]
    {:dispatch [::entities/apply-changes status entities]}))

;; Permissions for close/reopen
(rf/reg-sub ::can-close-bond?
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:session/user])])
  (fn [[bond user] _]
    (and bond
         (#{:bond.state/verified :bond.state/expired} (:domain/state bond))
         (permission-resolver/permitted? bond user :permission.bond/admin))))

(rf/reg-sub ::can-cancel-invite?
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:session/user])])
  (fn [[bond user] _]
    (and bond
         user
         (= :bond.state/pending-invite (:domain/state bond))
         (permission-resolver/permitted? bond user :permission.bond/admin))))

(rf/reg-sub ::can-reopen-bond?
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:session/user])])
  (fn [[bond _user] _]
    (and bond
         (= :bond.state/closed (:domain/state bond))
         (some #{:permission.bond/admin} (:granted-permissions bond)))))

;; Verify bond modal state
(def ^:private verify-state (interceptors/path ::verify))

(rf/reg-sub ::verify :-> ::verify)

(rf/reg-sub ::verify-open?
  :<- [::verify]
  (fn [{:keys [open? bond-id]} [_ entity-id]]
    (and open? (= entity-id bond-id))))

(rf/reg-sub ::can-verify-bond?
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:session/user])])
  (fn [[bond user] _]
    (and bond
         user
         (= (:domain/state bond) :bond.state/unverified)
         (permission-resolver/permitted? bond user :permission.bond/admin))))

(rf/reg-event-db ::open-verify verify-state
  (fn [state [_ bond]]
    (assoc state :open? true :bond-id (:db/id bond))))

(rf/reg-event-db ::close-verify verify-state
  (fn [_state _]
    {}))

(rf/reg-event-fx ::verify-bond
  (fn [_ [_ {:keys [bond-id] :as data}]]
    {:dispatch [:comm/command-monitored
                {:command    :bond/verify-bond
                 :method     :post
                 :data       data
                 :on-success [::verify-bond.success bond-id]
                 :notify     {:success :bond/verified-success}}]}))

(rf/reg-event-fx ::verify-bond.success
  (fn [_ [_ bond-id status entities]]
    {:fx [[:dispatch [::entities/apply-changes status entities]]
          [:dispatch [::delete-bond-verification-task bond-id]]
          [:dispatch [::close-verify]]]}))

(rf/reg-event-fx ::delete-bond-verification-task
  (fn [{:keys [db]} [_ bond-id]]
    (let [task-id (-> (entities/find-entities
                        {:entity-db (get-in db [::dashlets/dashlet-db task/default-task-dashlet-id :data])}
                        {:task/reference bond-id
                         :task/type      :task.type/bond-verification})
                      first
                      :db/id)]
      (when task-id
        {:dispatch [::dashlets/delete-item task/default-task-dashlet-id task-id]}))))

(rf/reg-sub ::edit-permissions
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:session/user])])
  (fn [[bond user] _]
    (when bond
      (let [owner?      (bond-model/bond-owner? bond user)
            unverified? ((du/in-state-fn :bond.state/unverified :bond.state/pending-invite) bond)
            perms       (:granted-permissions bond)
            can-edit?   (and (not (du/in-state? :bond.state/closed bond))
                             (some #{:permission.bond/modify :permission.bond/admin} perms))
            admin?      (some #{:permission.bond/admin} perms)]
        {:owner?             owner?
         :can-edit?          can-edit?
         :can-full-edit?     (and unverified? can-edit?)
         :can-extend?        (and can-edit?
                                  (or (not owner?)
                                      admin?
                                      (bond-model/owner-can-extend? bond)))
         :can-change-amount? (and can-edit?
                                  (or (not owner?)
                                      admin?
                                      (bond-model/owner-can-change-amount? bond)))}))))

(def ^:private edit-state (interceptors/path ::edit))

(rf/reg-sub ::edit :-> ::edit)

(rf/reg-event-db ::open-edit
  [edit-state]
  (fn [state [_ {:bond/keys [id amount expires-on owner
                             template external-id issuer-name
                             issuer-phone issuer-email description]
                 :as        bond}]]
    (let [unverified? ((du/in-state-fn :bond.state/unverified :bond.state/pending-invite) bond)]
      (cond-> (assoc state
                     :open? true
                     :editing-bond-id id
                     :bond/amount amount
                     :bond/expires-on expires-on
                     :reason ""
                     :saving? false
                     :show-errors? false
                     :full-edit? unverified?)
        unverified? (assoc :bond/template {:id (:db/id template)}
                           :bond/external-id external-id
                           :bond/issuer-name issuer-name
                           :bond/issuer-phone issuer-phone
                           :bond/issuer-email issuer-email
                           :bond/description description)
        (and unverified? owner) (assoc :bond/owner {:id (:db/id owner)})))))

(rf/reg-event-db ::close-edit
  [edit-state]
  (constantly {}))

(rf/reg-event-db ::set-amount
  [edit-state]
  (fn [state [_ v]]
    (assoc state :bond/amount v)))

(rf/reg-event-db ::set-expires-on
  [edit-state]
  (fn [state [_ v]]
    (assoc state :bond/expires-on v)))

(rf/reg-event-db ::set-reason
  [edit-state]
  (fn [state [_ v]]
    (assoc state :reason v)))

(rf/reg-event-db ::set-edit-field
  [edit-state]
  (fn [state [_ field value]]
    (assoc state field value)))

(rf/reg-sub ::edit-open? :<- [::edit] :-> :open?)
(rf/reg-sub ::edit-full? :<- [::edit] :-> :full-edit?)
(rf/reg-sub ::editing-bond-id :<- [::edit] :-> :editing-bond-id)
(rf/reg-sub ::edit-amount :<- [::edit] :-> :bond/amount)
(rf/reg-sub ::edit-expires-on :<- [::edit] :-> :bond/expires-on)
(rf/reg-sub ::edit-reason :<- [::edit] :-> :reason)
(rf/reg-sub ::edit-saving? :<- [::edit] :-> :saving?)

(rf/reg-sub ::edit-show-errors?
  :<- [::edit]
  :-> :show-errors?)

(defn- edit->form-data
  [edit]
  (select-keys edit (if (:full-edit? edit)
                      bond-model/bond-application-keys
                      [:bond/amount :bond/expires-on])))

(defn- full-edit->api-params
  "Convert full edit state to update-bond-application API parameters.
   Full edit is available only for unverified bonds."
  [edit bond-id version form-data->api-params]
  (assoc (form-data->api-params (edit->form-data edit))
         :bond-id bond-id
         :version version))

(defn- partial-edit->api-params
  "Convert partial edit state to update-bond API parameters.
   Partial edit is for verified bonds with field-level permissions.
   Edit state already uses :bond/* namespaced keys, so we just select-keys."
  [edit bond-id version]
  (merge {:bond-id bond-id
          :version version}
         (select-keys edit [:bond/amount :bond/expires-on :reason])))

(rf/reg-sub ::edit-form-data
  :<- [::edit]
  :-> edit->form-data)

(rf/reg-sub ::edit-group-required-bond-fields
  (fn [[_ bond-id]]
    (rf/subscribe [::bond bond-id]))
  (fn [bond _]
    (or (some-> bond :bond/group :group/bond-required-fields) #{})))

(rf/reg-sub ::edit-error-messages
  (fn [[_ bond-id]]
    [(rf/subscribe [::edit-group-required-bond-fields bond-id])
     (rf/subscribe [::bond bond-id])])
  (fn [[required-fields bond] _]
    (compute-effective-error-messages required-fields bond)))

(rf/reg-sub ::edit-validator
  (fn [[_ bond-id]]
    [(rf/subscribe [::edit-form-data])
     (rf/subscribe [::edit-error-messages bond-id])])
  (fn [[data error-messages] _]
    (validate/model-validator data error-messages)))

(rf/reg-sub ::full-edit-dirty?
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [::edit])])
  (fn [[bond edit] _]
    (bond-util/full-edit-dirty? bond edit)))

(rf/reg-event-db ::clear-saving
  [edit-state]
  (fn [state _]
    (dissoc state :saving?)))

(rf/reg-event-fx ::submit-edit
  (fn [{db :db} [_ bond required-fields]]
    (let [edit           (get db ::edit)
          version        (:entity/version bond)
          bond-entity-id (:db/id bond)]
      (if (:full-edit? edit)
        ;; Full Edit (Unverified/Pending-invite)
        (let [error-messages    (compute-effective-error-messages required-fields bond)
              form-data         (edit->form-data edit)
              validation-errors (validate/validate-form-strict form-data error-messages)
              pending-invite?   (du/in-state? :bond.state/pending-invite bond)
              command           (if pending-invite?
                                  :bond/update-pending-invite-bond
                                  :bond/update-bond-application)
              form->api-params  (if pending-invite?
                                  bond-util/pending-invite-form-data->api-params
                                  bond-util/form-data->api-params)]
          (if (seq validation-errors)
            {:db (assoc-in db [::edit :show-errors?] true)}
            {:db           (assoc-in db [::edit :saving?] true)
             :comm/command {:command          command
                            :method           :post
                            :data             (full-edit->api-params edit bond-entity-id version form->api-params)
                            :on-success       [::submit-edit.success]
                            :finally-dispatch [::clear-saving]}}))
        ;; Partial Edit (Verified)
        (let [error-messages    (compute-effective-error-messages nil bond)
              form-data         (edit->form-data edit)
              validation-errors (validate/validate-form form-data error-messages)]
          (if (seq validation-errors)
            {:db (assoc-in db [::edit :show-errors?] true)}
            {:db           (assoc-in db [::edit :saving?] true)
             :comm/command {:command          :bond/update-bond
                            :method           :post
                            :data             (partial-edit->api-params edit bond-entity-id version)
                            :on-success       [::submit-edit.success]
                            :finally-dispatch [::clear-saving]}}))))))

(rf/reg-event-fx ::submit-edit.success
  (fn [_ [_ status resp]]
    {:fx [[:dispatch [::close-edit]]
          [:dispatch [::entities/apply-changes status resp]]
          [:dispatch [:notifications/add :toast :success :success :bond/save-success]]]}))

(rf/reg-sub ::edit-applicant-select-opts
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:language])
     (rf/subscribe [:tr])])
  (fn [[bond lang tr] _]
    (when-let [owner (:bond/owner bond)]
      [{:name (bond-util/format-bond-owner lang owner tr)
        :id   (:db/id owner)}])))

(rf/reg-event-fx ::load-templates
  (fn [_ [_ bond-group-id]]
    (when bond-group-id
      {:comm/command {:command    :bond-template/get-all
                      :method     :get
                      :data       {:group-id bond-group-id}
                      :on-success [::load-templates.success]}})))

(rf/reg-sub ::edit-template-select-opts-full
  (fn [[_ bond-id]]
    [(rf/subscribe [::bond bond-id])
     (rf/subscribe [:language])
     (rf/subscribe [::entities/entities {:entity/type :entity.type/bond-template}])])
  (fn [[bond lang templates] _]
    (let [group-id         (get-in bond [:bond/group :db/id])
          current-template (:bond/template bond)
          base-templates   (->> templates
                                (filter #(= (get-in % [:bond-template/group :db/id]) group-id)))
          all-templates    (cond-> base-templates
                             (and current-template
                                  (not (some #(= (:db/id %) (:db/id current-template)) base-templates)))
                             (conj current-template))]
      (->> all-templates
           (map (fn [t] {:name (or (bond-model/template-name t lang) (str (:db/id t)))
                         :id   (:db/id t)}))
           (util/distinct-by :id)
           (sort-by :name)))))

(rf/reg-event-fx ::load-templates.success
  (fn [_ [_ status resp]]
    (let [templates (:entities resp)]
      (if (seq templates)
        {:dispatch [::entities/merge-entity-db status (du/entities->entity-db templates)]}
        {}))))
